import { useMemo } from "react";

import {
	draftSubtotal,
	formatPurchaseMoney,
	type PurchaseLineDraft,
} from "./hub-purchase-lines.js";
import { DocEditItemTableHead } from "./hub-purchase-document-table.js";

type SalesOrderLinesEditorProps = {
	drafts: PurchaseLineDraft[];
	onChange: (drafts: PurchaseLineDraft[]) => void;
	showTax: boolean;
	taxAmount: number;
	grandTotal: number;
	formatMoney: (amount: number) => string;
	idPrefix: string;
};

export function SalesOrderLinesEditor({
	drafts,
	onChange,
	showTax,
	taxAmount,
	grandTotal,
	formatMoney,
	idPrefix,
}: SalesOrderLinesEditorProps) {
	const subtotal = useMemo(() => draftSubtotal(drafts), [drafts]);
	function updateRow(key: string, patch: Partial<PurchaseLineDraft>) {
		onChange(drafts.map((row) => (row.key === key ? { ...row, ...patch } : row)));
	}
	function addRow() {
		if (drafts.length >= 50) return;
		onChange([...drafts, { key: `line-${Date.now()}`, description: "", qty: "1", unitPrice: "" }]);
	}
	function removeRow(key: string) {
		if (drafts.length <= 1) return;
		onChange(drafts.filter((row) => row.key !== key));
	}
	return (
		<section className="doc-edit-card" aria-labelledby={`${idPrefix}-items`}>
			<div className="doc-edit-section-head">
				<h2 id={`${idPrefix}-items`} className="doc-edit-kicker">Items</h2>
				<button className="doc-detail-btn" type="button" onClick={addRow} disabled={drafts.length >= 50}>Add line</button>
			</div>
			<div className="doc-edit-table-box">
				<table className="doc-edit-table">
					<thead>
						<DocEditItemTableHead rateLabel="Rate" showActions />
					</thead>
					<tbody>
						{drafts.map((row, index) => {
							const qty = Number(row.qty) || 1;
							const unit = Number(row.unitPrice) || 0;
							const lineTotal = formatPurchaseMoney(qty * unit);
							return (
								<tr key={row.key}>
									<td className="center">{index + 1}</td>
									<td>
										<label className="sr" htmlFor={`${idPrefix}-desc-${row.key}`}>Item</label>
										<div className="doc-edit-chip doc-edit-chip-grow">
											<textarea
												id={`${idPrefix}-desc-${row.key}`}
												rows={2}
												value={row.description}
												onChange={(event) => updateRow(row.key, { description: event.target.value })}
												required={index === 0}
												placeholder="Description"
											/>
										</div>
									</td>
									<td className="center">
										<input
											className="doc-edit-qty-input"
											type="number"
											min={1}
											step={1}
											inputMode="numeric"
											aria-label={`Quantity line ${index + 1}`}
											value={row.qty}
											onChange={(event) => updateRow(row.key, { qty: event.target.value })}
										/>
									</td>
									<td className="num">
										<label className="sr" htmlFor={`${idPrefix}-unit-${row.key}`}>Unit price</label>
										<input
											id={`${idPrefix}-unit-${row.key}`}
											className="doc-edit-money"
											inputMode="decimal"
											value={row.unitPrice}
											onChange={(event) => updateRow(row.key, { unitPrice: event.target.value })}
											required={index === 0}
										/>
									</td>
									<td className="num">
										<p className="doc-edit-line-total">{formatMoney(Number(lineTotal))}</p>
									</td>
									<td className="center">
										<button
											className="btn ghost"
											type="button"
											aria-label={`Remove line ${index + 1}`}
											disabled={drafts.length <= 1}
											onClick={() => removeRow(row.key)}
										>
											Remove
										</button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<dl className="doc-edit-summary-lines doc-edit-summary-lines-inline">
				<div><dt>Subtotal</dt><dd>{formatMoney(subtotal)}</dd></div>
				{showTax ? <div><dt>Tax</dt><dd>{formatMoney(taxAmount)}</dd></div> : null}
				<div className="grand"><dt>Total</dt><dd>{formatMoney(grandTotal)}</dd></div>
			</dl>
		</section>
	);
}
